import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

export interface Competitor {
  id: string;
  locationId: string;
  placeId: string | null;
  name: string;
  website: string | null;
  address: string | null;
  rating: number | null;
  reviewCount: number | null;
  latitude: number | null;
  longitude: number | null;
  placeTypes: string[];
  status: 'active' | 'dismissed';
  source: string;
  competitorLayer: 'DIRECT' | 'INDIRECT' | null;
  lastSyncedAt: string | null;
  distanceKm: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompetitionDensity {
  practiceCount: number;
  population: number;
  residentsPerPractice: number;
  nationalBenchmark: number;
  radius: number;
  /** The cached scan is past its TTL and a fresh one is running in the background. */
  refreshing?: boolean;
}

export interface CompetitorAnalysisCompetitor {
  id: string;
  name: string;
  rating: number | null;
  reviewCount: number | null;
  address: string | null;
  website: string | null;
  distanceKm: number | null;
  competitorLayer?: 'DIRECT' | 'INDIRECT' | null;
  strengths: string | null;
  gap: string | null;
  threatRationale: string | null;
}

export interface CompetitorInsight {
  headline: string;
  supportingEvidence: string[];
  strategicImplication: string;
}

export interface CompetitorAnalysisData {
  competitors: CompetitorAnalysisCompetitor[];
  insight: CompetitorInsight | null;
  feedback: string | null;
  generatedAt: string | null;
  isStale: boolean;
  /** An analysis is running now; the data is the last completed one. */
  running?: boolean;
}

export const competitorApi = {
  getCompetitors: (locationId: string) =>
    apiClient
      .get<
        // reviewed: the owner has saved their picks (keeping every competitor is a choice).
        ApiResponse<Competitor[]> & { reviewed?: boolean }
      >('/strategy/competitors', { params: { locationId } })
      .then((r) => r.data),

  discoverCompetitors: (locationId: string) =>
    apiClient
      // Queued: watch the competitor_discovery workflow for the result.
      .post<ApiResponse<{ workflowId: string | null; alreadyRunning: boolean }>>(
        '/strategy/competitors/discover',
        { locationId },
      )
      .then((r) => r.data),

  updateStatus: (id: string, status: 'active' | 'dismissed') =>
    apiClient
      .patch<ApiResponse<Competitor>>(`/strategy/competitors/${id}`, { status })
      .then((r) => r.data),

  /** Save the full selection: these ids active, every other competitor dismissed. */
  saveSelection: (locationId: string, activeIds: string[]) =>
    apiClient
      .post<ApiResponse<null>>('/strategy/competitors/selection', { locationId, activeIds })
      .then((r) => r.data),

  confirmList: (locationId: string) =>
    apiClient
      .post<ApiResponse<null>>('/strategy/competitors/confirm', { locationId })
      .then((r) => r.data),

  getPracticeCoords: (locationId: string) =>
    apiClient
      .get<
        ApiResponse<{
          lat: number;
          lng: number;
          rating: number | null;
          reviewCount: number | null;
        } | null>
      >('/strategy/competitors/coords', { params: { locationId } })
      .then((r) => r.data),

  getDensity: (locationId: string) =>
    apiClient
      .get<
        // null until a first scan for the current radius exists (it runs in the background)
        ApiResponse<CompetitionDensity | null>
      >('/strategy/competitors/density', { params: { locationId } })
      .then((r) => r.data),

  /** Retry the density scan after a failure (page loads stop retrying on their own). */
  scanDensity: (locationId: string) =>
    apiClient
      .post<ApiResponse<{ queued: boolean }>>('/strategy/competitors/density/scan', { locationId })
      .then((r) => r.data),

  getAnalysis: (locationId: string, cacheOnly = false) =>
    apiClient
      .get<
        ApiResponse<CompetitorAnalysisData>
      >('/strategy/competitors/analysis', { params: { locationId, ...(cacheOnly && { cacheOnly: 'true' }) } })
      .then((r) => r.data),

  regenerateAnalysis: (locationId: string) =>
    apiClient
      .post<
        ApiResponse<CompetitorAnalysisData>
      >('/strategy/competitors/analysis/regenerate', { locationId })
      .then((r) => r.data),

  submitInsightFeedback: (locationId: string, feedback: string) =>
    apiClient
      .post<ApiResponse<null>>('/strategy/competitors/analysis/feedback', { locationId, feedback })
      .then((r) => r.data),
};
